// ─── Esquema de etiquetas, macros y reglas de automatización ─────────────────
// Única fuente de los tipos de acción, eventos, campos de condición y topes que
// usan las tres pestañas de ajustes. Calca el contrato de
// services/helpdeskWorkflowService.js (y del plan del backend): si el contrato
// cambia, se toca acá y no en cada editor.
//
// Las etiquetas visibles van como claves en español: el componente las pasa por
// t() al pintarlas (una constante de módulo no puede llamar a useTranslation).

import { roleRoutes } from "../../../../utils/roles";

// ── Topes (los mismos que valida el backend) ─────────────────────────────────
export const LABEL_NAME_MAX = 40;
export const LABEL_DESCRIPTION_MAX = 140;
export const MAX_LABELS = 50;
export const MACRO_NAME_MAX = 60;
export const MAX_MACROS = 100;
export const RULE_NAME_MAX = 60;
export const MAX_RULES = 30;
export const MAX_ACTIONS = 10;
export const MAX_CONDITIONS = 10;
// Valores por condición de lista (mismo tope que AUTOMATION_CONDITION_VALUES_MAX del backend).
export const MAX_CONDITION_VALUES = 20;
// Ids por acción de etiquetas: mismo tope que PATCH conversations/:id/labels.
export const MAX_LABELS_PER_ACTION = 10;
// Texto de notas y mensajes: mismo tope que POST conversations/:id/notes.
export const TEXT_MAX = 4000;
export const CONDITION_TEXT_MAX = 200;
// Nota de cierre: mismo tope que el cierre manual en el backend (RESOLVE_NOTE_MAX).
export const RESOLVE_NOTE_MAX = 500;

// Sentinela del selector de carpeta: "Sin carpeta" viaja como folder_id null,
// pero un <select> no puede tener null como valor.
export const NO_FOLDER = "__none__";
// "Yo mismo": el backend resuelve 'self' al usuario que ejecuta la macro.
export const SELF_AGENT = "self";

// Quién edita la configuración compartida: el mismo criterio que
// QuickRepliesSettings y el rail (isSupervisor).
const SUPERVISOR_ROLES = new Set([
  roleRoutes.supervisor,
  roleRoutes.admin,
  roleRoutes.superadmin,
  roleRoutes.systemSuperadmin,
]);

export function isWorkflowSupervisor(user) {
  return SUPERVISOR_ROLES.has(user?.role);
}

/** Id de un documento del backend (`id`, o `_id` si llega sin transformar). */
export function idOf(doc) {
  return doc?.id ?? doc?._id ?? null;
}

// ── Acciones ─────────────────────────────────────────────────────────────────
// `macro`/`rule`: dónde se ofrece. send_message y resolve son SOLO de macros: una
// regla que escribe al contacto o cierra competiría con la IA (plan, §Automatización).
export const ACTION_DEFS = {
  add_labels: { label: "Agregar etiquetas", macro: true, rule: true },
  remove_labels: { label: "Quitar etiquetas", macro: true, rule: true },
  assign: { label: "Asignar a", macro: true, rule: true },
  set_folder: { label: "Mover a carpeta", macro: true, rule: true },
  add_note: { label: "Agregar nota privada", macro: true, rule: true },
  send_message: { label: "Enviar mensaje al contacto", macro: true, rule: false },
  resolve: { label: "Resolver el caso", macro: true, rule: false },
};

export const ACTION_ORDER = [
  "add_labels",
  "remove_labels",
  "assign",
  "set_folder",
  "add_note",
  "send_message",
  "resolve",
];

export const MACRO_ACTION_TYPES = ACTION_ORDER.filter((type) => ACTION_DEFS[type].macro);
export const RULE_ACTION_TYPES = ACTION_ORDER.filter((type) => ACTION_DEFS[type].rule);

/** Parámetros iniciales de una acción recién agregada. */
export function defaultParams(type) {
  switch (type) {
    case "add_labels":
    case "remove_labels":
      return { label_ids: [] };
    case "assign":
      return { agent_id: "" };
    case "set_folder":
      return { folder_id: null };
    case "add_note":
    case "send_message":
      return { content: "" };
    case "resolve":
      return {};
    default:
      return {};
  }
}

/**
 * Deja "resolver" como la ÚLTIMA acción y una sola vez: después de cerrar el
 * caso no hay nada más que hacerle, y el backend rechaza otro orden. Se aplica
 * al cargar una macro y en cada cambio del editor, así el orden inválido no
 * puede ni dibujarse.
 * @param {Array<{type:string, params?:object}>} actions
 */
export function normalizeMacroActions(actions) {
  const list = Array.isArray(actions) ? actions : [];
  const others = list.filter((a) => a?.type !== "resolve");
  const resolve = list.find((a) => a?.type === "resolve");
  return resolve ? [...others, resolve] : others;
}

/**
 * Error de una acción, o null si está completa.
 * @param {{type:string, params?:object}} action
 * @param {(key:string)=>string} t
 */
export function validateAction(action, t) {
  const params = action?.params || {};
  switch (action?.type) {
    case "add_labels":
    case "remove_labels": {
      const ids = Array.isArray(params.label_ids) ? params.label_ids : [];
      if (ids.length === 0) return t("Elige al menos una etiqueta.");
      if (ids.length > MAX_LABELS_PER_ACTION) return t("Máximo 10 etiquetas por acción.");
      return null;
    }
    case "assign":
      return params.agent_id ? null : t("Elige a quién se asigna.");
    case "set_folder":
      return null;
    case "add_note":
    case "send_message": {
      const content = (params.content || "").trim();
      if (!content) return t("Escribe el texto.");
      if ((params.content || "").length > TEXT_MAX) return t("El texto supera los 4000 caracteres.");
      return null;
    }
    case "resolve":
      return (params.note || "").length > RESOLVE_NOTE_MAX
        ? t("La nota supera los 500 caracteres.")
        : null;
    default:
      return t("Acción no reconocida.");
  }
}

/**
 * Normaliza los parámetros al formato del contrato antes de enviar: sin claves
 * vacías y con los textos recortados.
 */
export function serializeAction(action) {
  const params = action.params || {};
  switch (action.type) {
    case "add_labels":
    case "remove_labels":
      return { type: action.type, params: { label_ids: [...(params.label_ids || [])] } };
    case "assign":
      return { type: action.type, params: { agent_id: params.agent_id } };
    case "set_folder":
      return { type: action.type, params: { folder_id: params.folder_id || null } };
    case "add_note":
    case "send_message":
      return { type: action.type, params: { content: params.content.trim() } };
    case "resolve": {
      const note = (params.note || "").trim();
      return { type: action.type, params: note ? { note } : {} };
    }
    default:
      return { type: action.type, params };
  }
}

// ── Reglas de automatización ─────────────────────────────────────────────────
export const RULE_EVENTS = [
  { value: "conversation_created", label: "Se crea una conversación" },
  { value: "message_received", label: "Llega un mensaje del contacto" },
  { value: "conversation_reopened", label: "Se reabre un caso" },
];

export const RULE_EVENT_LABELS = Object.fromEntries(RULE_EVENTS.map((e) => [e.value, e.label]));

// Canales omnicanal donde corre el enganche (handleInboundMessage). El canal
// interno queda fuera de todo, como en el resto del helpdesk.
export const CONDITION_CHANNELS = [
  { value: "whatsapp", label: "WhatsApp" },
  { value: "whatsapp_unofficial", label: "WhatsApp no oficial" },
  { value: "messenger", label: "Messenger" },
  { value: "instagram", label: "Instagram" },
];

export const AUDIENCE_OPTIONS = [
  { value: "driver", label: "Repartidor" },
  { value: "merchant", label: "Comercio" },
  { value: "customer", label: "Cliente" },
];

// Campo → operadores y tipo de valor. `kind`: multi (lista), text, label (lista
// de ids de etiqueta) o bool. Mismo contrato que valida el backend.
export const CONDITION_DEFS = {
  channel: { label: "Canal", kind: "multi", operators: ["in"] },
  account_key: { label: "Número o cuenta", kind: "multi", operators: ["in"] },
  message_content: {
    label: "Texto del mensaje",
    kind: "text",
    operators: ["contains", "not_contains"],
  },
  // Etiquetas: el backend evalúa `in` sobre una lista ("tiene alguna de" / "no
  // tiene ninguna de"); el editor elige una sola y la manda como lista de uno.
  has_label: { label: "Tiene la etiqueta", kind: "label", operators: ["in"] },
  not_has_label: { label: "No tiene la etiqueta", kind: "label", operators: ["in"] },
  has_commerce: { label: "Comercio identificado", kind: "bool", operators: ["is"] },
  business_hours: { label: "Horario de atención", kind: "bool", operators: ["is"] },
  audience: { label: "Quién escribe", kind: "multi", operators: ["in"] },
};

export const CONDITION_ORDER = [
  "channel",
  "account_key",
  "message_content",
  "has_label",
  "not_has_label",
  "has_commerce",
  "business_hours",
  "audience",
];

export const OPERATOR_LABELS = {
  in: "es alguno de",
  contains: "contiene",
  not_contains: "no contiene",
  is: "es",
};

// Textos de las opciones sí/no por campo: "dentro/fuera" se entiende mejor que
// "sí/no" para el horario.
export const BOOL_OPTION_LABELS = {
  has_commerce: { true: "Sí", false: "No" },
  business_hours: { true: "Dentro del horario", false: "Fuera del horario" },
};

/** Condición nueva con el primer operador y un valor vacío de su tipo. */
export function defaultCondition(field) {
  const def = CONDITION_DEFS[field];
  const value = def.kind === "multi" || def.kind === "label" ? [] : def.kind === "bool" ? true : "";
  return { field, operator: def.operators[0], value };
}

/**
 * Error de una condición, o null si está completa.
 * @param {{field:string, operator:string, value:any}} condition
 * @param {(key:string)=>string} t
 */
export function validateCondition(condition, t) {
  const def = CONDITION_DEFS[condition?.field];
  if (!def) return t("Condición no reconocida.");
  if (!def.operators.includes(condition.operator)) return t("Operador no válido.");
  switch (def.kind) {
    case "multi":
      return Array.isArray(condition.value) && condition.value.length > 0
        ? null
        : t("Elige al menos una opción.");
    case "text": {
      const text = String(condition.value || "").trim();
      if (!text) return t("Escribe el texto a buscar.");
      if (text.length > CONDITION_TEXT_MAX) return t("Máximo 200 caracteres.");
      return null;
    }
    case "label":
      return Array.isArray(condition.value) && condition.value.length > 0
        ? null
        : t("Elige una etiqueta.");
    case "bool":
      return typeof condition.value === "boolean" ? null : t("Elige una opción.");
    default:
      return null;
  }
}

export function serializeCondition(condition) {
  const def = CONDITION_DEFS[condition.field];
  const value =
    def.kind === "text" ? String(condition.value).trim() : condition.value;
  return { field: condition.field, operator: condition.operator, value };
}
